import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { Cliente, TipoCliente } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import {
  CampoBusqueda,
  Campo,
  Input,
  Select,
} from "../../components/Formulario";
import { Modal } from "../../components/Modal";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import { PresupuestadorHonorarios } from "../economia/PresupuestadorHonorarios";

const TIPOS_CLIENTE: TipoCliente[] = ["Persona Física", "Persona Jurídica"];

const VALORES_INICIALES = {
  tipo_cliente: "Persona Física" as TipoCliente,
  nombre_completo: "",
  identificacion: "",
  email: "",
  telefono: "",
  domicilio: "",
  localidad: "",
};

export function ClientesPage() {
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState<Cliente | null>(null);
  const [valores, setValores] = useState(VALORES_INICIALES);
  const [guardando, setGuardando] = useState(false);
  const [archivarCliente, setArchivarCliente] = useState<Cliente | null>(null);
  const [archivando, setArchivando] = useState(false);
  const [presupuestarCliente, setPresupuestarCliente] = useState<Cliente | null>(null);

  function cargar() {
    setCargando(true);
    api
      .get<{ items: Cliente[] }>(
        `/clientes?busqueda=${encodeURIComponent(busqueda)}`,
      )
      .then((d) => setClientes(d.items))
      .catch(() => mostrar("No se pudieron cargar los clientes.", "error"))
      .finally(() => setCargando(false));
  }

  useEffect(() => {
    const debounce = setTimeout(cargar, 250);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda]);

  function abrirCrear() {
    setEditando(null);
    setValores(VALORES_INICIALES);
    setModalAbierto(true);
  }

  function abrirEditar(cliente: Cliente) {
    setEditando(cliente);
    setValores({
      tipo_cliente: cliente.tipo_cliente,
      nombre_completo: cliente.nombre_completo,
      identificacion: cliente.identificacion ?? "",
      email: cliente.email ?? "",
      telefono: cliente.telefono ?? "",
      domicilio: cliente.domicilio ?? "",
      localidad: cliente.localidad ?? "",
    });
    setModalAbierto(true);
  }

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      if (editando) {
        await api.put(`/clientes/${editando.id}`, valores);
        mostrar("Cliente actualizado.", "exito");
      } else {
        await api.post("/clientes", valores);
        mostrar("Cliente creado.", "exito");
      }
      setModalAbierto(false);
      cargar();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo guardar el cliente.",
        "error",
      );
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarArchivar() {
    if (!archivarCliente) return;
    setArchivando(true);
    try {
      await api.delete(`/clientes/${archivarCliente.id}`);
      mostrar("Cliente archivado.", "exito");
      setArchivarCliente(null);
      cargar();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo archivar el cliente.",
        "error",
      );
    } finally {
      setArchivando(false);
    }
  }

  const columnas: ColumnaTabla<Cliente>[] = [
    {
      encabezado: "Nombre",
      render: (c) => (
        <button
          onClick={() => navegar(`/clientes/${c.id}`)}
          className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
        >
          {c.nombre_completo}
        </button>
      ),
    },
    { encabezado: "Tipo", render: (c) => c.tipo_cliente },
    { encabezado: "Identificación", render: (c) => c.identificacion ?? "—" },
    { encabezado: "Email", render: (c) => c.email ?? "—" },
    { encabezado: "Teléfono", render: (c) => c.telefono ?? "—" },
    {
      encabezado: "Acciones",
      render: (c) => (
        <div className="flex gap-3">
          <button
            onClick={() => abrirEditar(c)}
            className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
          >
            Editar
          </button>
          <button
            onClick={() => setPresupuestarCliente(c)}
            className="font-medium text-secundario transition-colors hover:underline"
          >
            Presupuestar honorarios
          </button>
          <button
            onClick={() => setArchivarCliente(c)}
            className="font-medium text-error transition-colors hover:underline"
          >
            Archivar
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <EncabezadoPagina
        titulo="Clientes"
        descripcion="Personas físicas y jurídicas del estudio"
        acciones={<Boton onClick={abrirCrear}>+ Nuevo Cliente</Boton>}
      />

      <div className="mb-4">
        <CampoBusqueda
          valor={busqueda}
          onCambio={setBusqueda}
          placeholder="Buscar por nombre o identificación..."
        />
      </div>

      <Tabla
        columnas={columnas}
        filas={clientes}
        claveFila={(c) => c.id}
        cargando={cargando}
        tituloVacio="No hay clientes"
        descripcionVacio="Creá el primer cliente del estudio."
      />

      <Modal
        titulo={editando ? "Editar cliente" : "Nuevo cliente"}
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
      >
        <form onSubmit={manejarEnvio} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Tipo" obligatorio>
              <Select
                value={valores.tipo_cliente}
                onChange={(e) =>
                  setValores((v) => ({
                    ...v,
                    tipo_cliente: e.target.value as TipoCliente,
                  }))
                }
              >
                {TIPOS_CLIENTE.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Campo>
            <Campo etiqueta="Nombre completo / Razón social" obligatorio>
              <Input
                required
                value={valores.nombre_completo}
                onChange={(e) =>
                  setValores((v) => ({ ...v, nombre_completo: e.target.value }))
                }
              />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Identificación (DNI/CUIT)">
              <Input
                value={valores.identificacion}
                onChange={(e) =>
                  setValores((v) => ({ ...v, identificacion: e.target.value }))
                }
              />
            </Campo>
            <Campo etiqueta="Email">
              <Input
                type="email"
                value={valores.email}
                onChange={(e) =>
                  setValores((v) => ({ ...v, email: e.target.value }))
                }
              />
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo etiqueta="Teléfono">
              <Input
                value={valores.telefono}
                onChange={(e) =>
                  setValores((v) => ({ ...v, telefono: e.target.value }))
                }
              />
            </Campo>
            <Campo etiqueta="Localidad">
              <Input
                value={valores.localidad}
                onChange={(e) =>
                  setValores((v) => ({ ...v, localidad: e.target.value }))
                }
              />
            </Campo>
          </div>
          <Campo etiqueta="Domicilio">
            <Input
              value={valores.domicilio}
              onChange={(e) =>
                setValores((v) => ({ ...v, domicilio: e.target.value }))
              }
            />
          </Campo>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalAbierto(false)}
              disabled={guardando}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <ConfirmacionDialog
        abierto={archivarCliente !== null}
        titulo="Archivar cliente"
        mensaje={`¿Archivar a "${archivarCliente?.nombre_completo}"? Podrás seguir viendo sus expedientes existentes.`}
        cargando={archivando}
        onConfirmar={confirmarArchivar}
        onCancelar={() => setArchivarCliente(null)}
        textoConfirmar="Archivar"
        peligroso
      />

      <PresupuestadorHonorarios
        abierto={presupuestarCliente !== null}
        onCerrar={() => setPresupuestarCliente(null)}
        clienteIdInicial={presupuestarCliente?.id}
      />
    </div>
  );
}
